import React from 'react';
import moment from 'moment';
import safeIsEmpty from 'lodash/isEmpty';
import { Box } from 'components/commons';
import { LoanApplication } from 'generated/graphql';
import Loading from 'components/loading';
import { recalculateSimulationResult } from 'utils/simulation-utils';
import { formatMoney, formatMoneyWithoutDecimals } from 'utils/utils';
import hurry from 'assets/img/hurry-up.png';
import './bizon-sidebar.styles.scss';

type BizonSidebar = {
    loanApplication?: LoanApplication,
    className?: string,
    mainTitle?: JSX.Element,
    boxTitle?: JSX.Element,
    initialClosed?: boolean,
    isCompleted?: boolean,
};

const Wrapper: React.FC<{ className?: string }> = ({ className, children }): JSX.Element => (
    <div className={`bizon-sidebar ${className || ''}`.trim()}>
        <div className="board">
            {children}
        </div>
    </div>
);

const BizonSidebar: React.FC<BizonSidebar> = ({ loanApplication, className, mainTitle, boxTitle, initialClosed = true, isCompleted = false }): JSX.Element => {
    if (safeIsEmpty(loanApplication) && isCompleted) {
        return (
            <Wrapper className={className}>
                <Loading />
            </Wrapper>
        );

    }

    const insurance = loanApplication?.tracking?.insuranceOptIn?.accepted ?? false;
    const calculus = loanApplication && recalculateSimulationResult(loanApplication, [], insurance, loanApplication.product.bizId);

    const hiddenContent = (
        <>
            {isCompleted && loanApplication ?
                <div className="proposal-box__hidden">
                    {loanApplication?.tracking?.insuranceOptIn?.accepted &&
                        <>
                            <div className="proposal-box__hidden--box-subitem">
                                <h4 className="body-2">Seguro</h4>
                            </div>
                            <div className="proposal-box__hidden--box-subitem text-right">
                                <h4 className="body-2">R$ {formatMoney(calculus.insurancePremium)}</h4>
                            </div>
                        </>
                    }
                    {loanApplication?.acceptedProposal?.downPayments && loanApplication?.acceptedProposal?.downPayments > 0
                        ? <>
                            <div className="proposal-box__hidden--box-subitem">
                                <h4 className="body-2">{loanApplication?.acceptedProposal?.downPayments} parcelas de entrada</h4>
                            </div>
                            <div className="proposal-box__hidden--box-subitem text-right">
                                <h4 className="body-2">R$ {formatMoney(calculus.downPaymentPmt)}</h4>
                            </div>
                        </>
                        : <></>
                    }
                    <div className="proposal-box__hidden--box-subitem">
                        <h4 className="body-2">{loanApplication?.acceptedProposal?.tenure} parcelas</h4>
                    </div>
                    <div className="proposal-box__hidden--box-subitem text-right">
                        <h4 className="body-2">R$ {formatMoney(calculus.pmt)}</h4>
                    </div>
                    <div className="proposal-box__hidden--box-subitem">
                        <h4 className="body-2">Vencimento mensal</h4>
                        <p className="small">boletos gerados mensalmente</p>
                    </div>
                    <div className="proposal-box__hidden--box-subitem text-right">
                        <h4 className="body-2">{moment(loanApplication?.acceptedProposal?.paymentDay).format('[Dia] DD')}</h4>
                    </div>
                </div>
                :
                <>
                    <div className="contacts-box__hidden">
                        <div className="contacts-box__hidden--box-subitem mt-16">
                            <h4 className="body-2">Capitais e Regiões Metropolitanas:</h4>
                            <p>3609-3737</p>
                        </div>
                    </div>
                    <div className="contacts-box__hidden">
                        <div className="contacts-box__hidden--box-subitem">
                            <h4 className="body-2">Demais Localidades:</h4>
                            <p>(21) 3609-3737</p>
                        </div>
                    </div>
                </>
            }
        </>
    );

    return (
        <Wrapper className={className}>
            {mainTitle && (
                <div className="body-2 wrapper-maintitle">
                    <img src={hurry} alt="hurry" />
                    <span>{mainTitle}</span>
                </div>
            )}
            <Box className="proposal-box" {...{ initialClosed, hiddenContent }}>
                <p className={`${isCompleted ? "proposal" : "contacts"}-box__title caption medium m-0`}>
                    {boxTitle}
                    {isCompleted && loanApplication &&
                        <span className="title-2 bold mt-2">
                            R$ {formatMoneyWithoutDecimals(loanApplication?.acceptedProposal?.amount)}
                        </span>
                    }
                </p>
                {!isCompleted &&
                    <div className="contacts-box__hidden">
                        <div className="contacts-box__hidden--box-subitem">
                            <h4 className="body-2">WhatsApp:</h4>
                            <a href='https://wa.me/551153300448/' target='_blank'>(11) 5330-0448</a>
                        </div>
                    </div>
                }
            </Box>
        </Wrapper>
    );
};

export default BizonSidebar;
